<template lang="pug">
  v-container.fill-height.align-start.flex-column.pa-0.overflow-y-hidden
    v-snackbar( v-model="showError" color="error" ) {{ errorMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="showError = false")
          v-icon fal fa-times
    v-snackbar( v-model="showMessage" color="success" ) {{message}}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="showMessage = false")
          v-icon fal fa-times
    FoodDialog(v-if="showFoodDialog" :showFoodDialog="showFoodDialog"
      :tagCollection="tagCollection" :currentFoodItem="currentFoodItem"
      :itemCollection="itemCollection" :allergensList="allergensList"
      :isSelectionTagReadOnly="false"
      @closeDialog="closeFoodDialog"
      @fetchData="fetchData")

    v-card-title(style=" padding: 23px 0 ") {{ $t('foodDatabasePage.pageTitle') }}

    v-skeleton-loader.sloader(v-if="showSkeletonLoader" type="card, list-item-two-line@5")
    template(v-else).overflow-hidden
      v-container.flex-shrink-1.flex-grow-0.ma-0.pa-0(fluid)
        v-toolbar(dense flat)
          v-text-field.mr-3(
            v-model="filterText"
            clearable clear-icon="fal fa-times" hide-details :placeholder="filterTextPlaceholder"
            prepend-inner-icon="fas fa-search" single-line outlined
          @input="onFilterTextInput"
          )
          v-spacer
          v-btn(color="primary" :fab="!showExtended" :rounded="showExtended" @click="onAddNewFoodButtonClick")
            v-icon fal fa-plus
            span.ml-2(v-if="showExtended") {{ $t('foodDatabasePage.addFoodButtonLabel') }}

      v-container.flex-shrink-1.flex-grow-0(fluid)
        v-row
          v-icon.ml-4.mr-2 fal fa-tags
          v-item-group(v-model="filterTag" group multiple @change="onFilterTagChange")
            v-item(
              v-for="(tag, idx) in tagCollection" :key="idx"
              v-slot:default="{ active, toggle }"
              :value="tag"
            )
              v-chip.ma-1(
                active-class="primary" rounded
              :input-value="active"
                @click="toggle"
              )
                v-icon.mr-2 {{ tagButtonIcon(tag) }}
                | {{ tagButtonLabel(tag) }}

      v-container.flex-shrink-0.flex-grow-1.ma-0.pa-0(ref="listContainer" fluid)
        v-divider
        v-container.d-flex.flex-column.fill-height.justify-center(v-if="filteredItemCollection.length === 0" )
          div.text-center.text-h6 {{ $t('foodDatabasePage.noFoodInDatabaseText') }}
        v-list.overflow-y-auto(v-else :style="{height: virtualScrollHeight + 'px'}" )
          v-list-item( v-for="(item, idx) in filteredItemCollection" :key="idx" @click="onFoodItemClick(item)" )
            v-list-item-icon
              v-icon {{ foodItemIcon(item) }}
            v-list-item-content {{ item.name }}

</template>

<script lang="ts">
import Vue from 'vue'
import _, {reject} from 'lodash'
import {
  FETCH_ALLERGEN_LIST,
  FETCH_TAGS,
  FOOD_DATABASE_FETCH_FOOD_ITEMS
} from '@/store/action-types'
import FoodItem from '@/model/food-database/FoodItem'
import Tag from '@/model/tennant/Tag'
import {TranslateResult} from 'vue-i18n'
import {Component, Watch} from "vue-property-decorator"
import {Action} from "vuex-class";
import Allergen from "@/model/food-database/Allergen";
import FoodDialog from "@/components/dailymenu/FoodDialog.vue";

@Component({
  components: {
    FoodDialog
  }
})

export default class FoodDatabasePage extends Vue {

  @Action(FETCH_ALLERGEN_LIST) fetchAllergenList!: ({ (): Promise<Allergen[]> })

  private itemCollection: FoodItem[] | null = null
  private tagCollection: Tag[] | null = null
  private filteredItemCollection: FoodItem[] = []
  private filterText: string = ''
  private filterTag: Tag[] = []
  private selectedTagCollection: Tag[] = []
  private allergensList: Allergen[] | null = null
  private energyValueStatus: string = ''
  private fetchInProgress: boolean = false
  private showError: boolean = false
  private errorMessage: TranslateResult | null = null
  private currentFoodItem: FoodItem | null = null
  private foodItemName: string = ''
  private showMessage: boolean = false
  private message: TranslateResult | null = null
  private virtualScrollHeight: number = 0
  private showFoodDialog: boolean = false

  get showSkeletonLoader(): boolean {
    return !this.itemCollection && !this.tagCollection
  }

  get showExtended(): boolean {
    return this.$vuetify.breakpoint.mdAndUp
  }

  get filterTextPlaceholder(): TranslateResult {
    if (this.showExtended) {
      return this.$t('foodDatabasePage.searchFiledLabelExtended')
    }

    return this.$t('foodDatabasePage.searchFiledLabelShort')
  }

  get dialogTitle(): TranslateResult | null {
    if (this.currentFoodItem && this.currentFoodItem.id > 0) {
      return this.$t('foodDatabasePage.editDialogExistingFoodTitle')
    }

    return this.$t('foodDatabasePage.editDialogNewFoodTitle')
  }

  @Watch('showSkeletonLoader')
  watchShowSkeletonLoader (newVal: boolean, oldVal: boolean) {
    if (!newVal && oldVal) {
      this.$nextTick(this.calculateVirtualScrollHeight)
    }
  }

  foodItemIcon(foodItem: FoodItem): string | null {
    return _.head(foodItem.tags)?.iconClass  ? `fas ${_.head(foodItem.tags)!.iconClass}` : null
  }

  tagButtonIcon(tagItem: Tag): string | null {
    return tagItem.iconClass ? `fas ${tagItem.iconClass}` : null
  }

  tagButtonLabel(tagItem: Tag): string {
    const count = this.itemCollection!
        .filter((i: FoodItem) => {
          if (this.filterText) {
            const name = i.name
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()

            const filterText = this.filterText
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()

            return name.includes(filterText)
          }

          return true
        })
        .filter(i => !!_.find(i.tags, t => t.id === tagItem.id)).length

    if (!this.showExtended) {
      return `${count}`
    }

    return `${tagItem.title} (${count})`
  }

  filterItemCollection(): FoodItem[] {
    if (this.itemCollection) {
      return _.chain(this.itemCollection)
          .filter((i: FoodItem) => {
            if (this.filterTag.length) {
              const tagIds = i.tags.map((t: Tag) => t.id)
              const filterTagIds = this.filterTag.map((t: Tag) => t.id)

              return _.intersection(tagIds, filterTagIds).length > 0
            }

            return true
          })
          .filter((i: FoodItem) => {
            if (this.filterText) {
              const name = i.name
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()

              const filterText = this.filterText
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()

              return name.includes(filterText)
            }

            return true
          })
          .sort((a: FoodItem, b: FoodItem) => a.name.localeCompare(b.name, 'sk')) // FIXME
          .value()
    }

    return []
  }

  async fetchData(): Promise<void> {
    const promises = [
      await this.$store.dispatch(FOOD_DATABASE_FETCH_FOOD_ITEMS).catch(() => {
        this.showError = true
        this.errorMessage = this.$t('errors.fetchDataError').toString()
        return []
      }),
      await this.$store.dispatch(FETCH_TAGS).catch(() => {
        this.showError = true
        this.errorMessage = this.$t('errors.fetchDataError').toString()
        return []
      })
    ]

    const [
      foodItemCollection,
      tagCollection
    ] = await Promise.all(promises as Promise<Object[]>[]) as [FoodItem[], Tag[]]

    this.itemCollection = foodItemCollection
    this.tagCollection = tagCollection
    this.filteredItemCollection = this.filterItemCollection()
    //Promise.all([await this.$store.dispatch(FOOD_DATABASE_FETCH_FOOD_ITEMS).catch(() => {console.log(1)}), await this.$store.dispatch(FETCH_TAGS)]).then(() => {console.log('ok')}).catch(() => {console.log('no')})
  }

  onFilterTextInput(){

  }

  onFilterTagChange(): void {
    this.filteredItemCollection = this.filterItemCollection()
  }

  async closeFoodDialog(actionStatus: string, foodItem: FoodItem | null): Promise<void> {
    this.showFoodDialog = false
    this.currentFoodItem = null
    this.itemCollection = await this.$store.dispatch(FOOD_DATABASE_FETCH_FOOD_ITEMS).catch(() => {
      this.showError = true
      this.errorMessage = this.$t('errors.fetchDataError').toString()
      return []
    })
    this.filteredItemCollection = this.filterItemCollection()
  }

  onFoodItemClick(item: FoodItem): void {
    this.currentFoodItem = item
    this.showFoodDialog = true
  }

  onAddNewFoodButtonClick(): void {
    const unit: string | null = localStorage.getItem('energyUnit')
    this.currentFoodItem = FoodItem.createEmpty()
    this.energyValueStatus = unit ? unit : ''
    this.showFoodDialog = true

    this.selectedTagCollection = []
    this.foodItemName = this.currentFoodItem.name

    if (this.$refs['foodForm']) {
      //@ts-ignore
      this.$refs['foodForm'].resetValidation()
    }
  }

  calculateVirtualScrollHeight (): void {
    let height = 0

    if (this.$refs.listContainer) {
      height = (this.$refs.listContainer as HTMLElement).clientHeight
    }

    this.virtualScrollHeight = height
  }

  async fetchAllergens ():Promise<void> {
    try{
      this.fetchInProgress = true
      this.allergensList = await this.fetchAllergenList()
    }catch (e) {
      this.showError = true
      this.errorMessage = this.$t('errors.fetchDataError').toString()
    }finally {
      this.fetchInProgress = false

    }
  }

  mounted(): void {
    this.onFilterTextInput = _.debounce(() => {
      this.filteredItemCollection = this.filterItemCollection()
    }, 500)
    this.fetchData()
    this.fetchAllergens()
  }
}
</script>

<style lang="stylus" scoped>
.sloader
  width 100%

.food-list .v-icon
  width 30px

</style>